import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  TrendingUp as TrendingUpIcon,
  CloudQueue as WeatherIcon,
  Compare as CompareIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface PricingRule {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
  priority: number;
  ruleType: string;
  conditions: any;
  adjustment: any;
  abTesting?: any;
  performance?: any;
  mlOptimization?: any;
  externalData?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdvancedPricingPage: React.FC = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [tabValue, setTabValue] = useState(0);
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);
  const [competitorAnalysis, setCompetitorAnalysis] = useState<any>(null);
  const [weatherImpact, setWeatherImpact] = useState<any>(null);
  const [abTestResults, setAbTestResults] = useState<any[]>([]);

  useEffect(() => {
    fetchPricingRules();
    fetchCompetitorAnalysis();
    fetchWeatherImpact();
    fetchAbTestResults();
  }, []);

  const fetchPricingRules = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/pricing/rules', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRules(data);
      }
    } catch (error) {
      setError('Failed to fetch pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitorAnalysis = async () => {
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dayAfter = new Date();
      dayAfter.setDate(dayAfter.getDate() + 2);

      const response = await fetch(
        `/api/pricing/competitor-analysis?checkIn=${tomorrow.toISOString()}&checkOut=${dayAfter.toISOString()}&siteType=RV`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCompetitorAnalysis(data);
      }
    } catch (error) {
      console.error('Failed to fetch competitor analysis:', error);
    }
  };

  const fetchWeatherImpact = async () => {
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      const response = await fetch(
        `/api/pricing/weather-impact/64f8b8f0e5d4c2a1f9876543?date=${tomorrow.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setWeatherImpact(data);
      }
    } catch (error) {
      console.error('Failed to fetch weather impact:', error);
    }
  };

  const fetchAbTestResults = async () => {
    try {
      const response = await fetch('/api/pricing/ab-test-performance', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAbTestResults(data);
      }
    } catch (error) {
      console.error('Failed to fetch A/B test results:', error);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setOpenDialog(true);
  };

  const handleEditRule = (rule: PricingRule) => {
    setEditingRule(rule);
    setOpenDialog(true);
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      const response = await fetch(`/api/pricing/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Pricing rule deleted successfully');
        fetchPricingRules();
      }
    } catch (error) {
      setError('Failed to delete pricing rule');
    }
  };

  const handleToggleRule = async (ruleId: string) => {
    try {
      const response = await fetch(`/api/pricing/rules/${ruleId}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPricingRules();
      }
    } catch (error) {
      setError('Failed to toggle pricing rule');
    }
  };

  const startAdvancedServices = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/pricing/services/start', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Advanced pricing services started successfully');
      }
    } catch (error) {
      setError('Failed to start advanced pricing services');
    } finally {
      setLoading(false);
    }
  };

  const getRuleTypeIcon = (ruleType: string) => {
    switch (ruleType) {
      case 'Weather': return <WeatherIcon />;
      case 'Competitor': return <CompareIcon />;
      case 'Yield Management': return <TrendingUpIcon />;
      case 'Complex': return <AnalyticsIcon />;
      default: return <SettingsIcon />;
    }
  };

  const getRuleTypeColor = (ruleType: string) => {
    switch (ruleType) {
      case 'Weather': return 'info';
      case 'Competitor': return 'warning';
      case 'Yield Management': return 'success';
      case 'Complex': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Advanced Pricing Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={startAdvancedServices}
            sx={{ mr: 2 }}
            startIcon={<PlayIcon />}
          >
            Start Services
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRule}
            startIcon={<AddIcon />}
          >
            Create Advanced Rule
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Advanced Rules" />
          <Tab label="Competitor Analysis" />
          <Tab label="Weather Impact" />
          <Tab label="A/B Testing" />
          <Tab label="Performance Analytics" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {rules.filter(rule => ['Weather', 'Competitor', 'Complex', 'Yield Management'].includes(rule.ruleType)).map((rule) => (
            <Grid item xs={12} md={6} lg={4} key={rule._id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getRuleTypeIcon(rule.ruleType)}
                    <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                      {rule.name}
                    </Typography>
                    <Chip
                      label={rule.ruleType}
                      color={getRuleTypeColor(rule.ruleType) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {rule.description}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2">
                      Priority: {rule.priority}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={rule.isActive}
                          onChange={() => handleToggleRule(rule._id)}
                          size="small"
                        />
                      }
                      label="Active"
                    />
                  </Box>

                  {rule.performance && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Applied: {rule.performance.timesApplied} times
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Revenue Impact: ${rule.performance.totalRevenueImpact?.toFixed(2) || '0.00'}
                      </Typography>
                      {rule.performance.effectivenessScore && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            Effectiveness:
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={rule.performance.effectivenessScore}
                            sx={{ flexGrow: 1, mr: 1 }}
                          />
                          <Typography variant="body2">
                            {rule.performance.effectivenessScore}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {rule.abTesting?.enabled && (
                    <Chip
                      label={`A/B Test: ${rule.abTesting.variant}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  )}

                  {rule.mlOptimization?.enabled && (
                    <Chip
                      label="ML Optimized"
                      color="secondary"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                </CardContent>

                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => handleEditRule(rule)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteRule(rule._id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <CompareIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Competitor Analysis
                </Typography>
                
                {competitorAnalysis ? (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Average Price:</strong> ${competitorAnalysis.averagePrice || 'N/A'}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Price Range:</strong> ${competitorAnalysis.minPrice || 'N/A'} - ${competitorAnalysis.maxPrice || 'N/A'}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Competitors Analyzed:</strong> {competitorAnalysis.competitorCount || 0}
                    </Typography>
                    <Alert severity="info">
                      {competitorAnalysis.recommendation || 'No recommendation available'}
                    </Alert>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No competitor data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Market Position Strategy
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Based on current competitor analysis
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Chip label="Value Strategy" color="success" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="Competitive Strategy" color="warning" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="Premium Strategy" color="error" sx={{ mb: 1 }} />
                </Box>

                <Button variant="outlined" fullWidth>
                  Update Competitor Monitoring
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <WeatherIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Weather Impact Analysis
                </Typography>
                
                {weatherImpact ? (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Price Multiplier:</strong> {weatherImpact.multiplier}x
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Reason:</strong> {weatherImpact.reason}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Confidence:</strong> {Math.round((weatherImpact.confidence || 0) * 100)}%
                    </Typography>
                    
                    {weatherImpact.weatherData && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Weather Details:
                        </Typography>
                        <Typography variant="body2">
                          Conditions: {weatherImpact.weatherData.conditions}
                        </Typography>
                        <Typography variant="body2">
                          Temperature: {weatherImpact.weatherData.temperature}°F
                        </Typography>
                        <Typography variant="body2">
                          Precipitation: {weatherImpact.weatherData.precipitationChance}%
                        </Typography>
                        <Typography variant="body2">
                          Quality Score: {weatherImpact.weatherData.qualityScore}/100
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No weather data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Weather-Based Pricing Rules
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Automated adjustments based on weather conditions
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • Excellent weather: +15% premium
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • Good weather: +5% premium
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • Poor weather: -15% discount
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • Severe weather: -20% discount
                  </Typography>
                </Box>

                <Button variant="outlined" fullWidth>
                  Configure Weather Rules
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              A/B Testing Results
            </Typography>
            
            {abTestResults.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Test Name</TableCell>
                      <TableCell>Variant</TableCell>
                      <TableCell>Split %</TableCell>
                      <TableCell>Conversion Rate</TableCell>
                      <TableCell>Avg Booking Value</TableCell>
                      <TableCell>Total Revenue</TableCell>
                      <TableCell>Bookings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {abTestResults.map((test) => (
                      <TableRow key={test.ruleId}>
                        <TableCell>{test.testName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={test.variant} 
                            color={test.variant === 'A' ? 'primary' : 'secondary'}
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{test.splitPercent}%</TableCell>
                        <TableCell>
                          {test.metrics?.conversionRate ? 
                            `${(test.metrics.conversionRate * 100).toFixed(2)}%` : 
                            'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          ${test.metrics?.averageBookingValue?.toFixed(2) || '0.00'}
                        </TableCell>
                        <TableCell>
                          ${test.metrics?.totalRevenue?.toFixed(2) || '0.00'}
                        </TableCell>
                        <TableCell>{test.metrics?.bookingCount || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No A/B tests currently running
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Pricing Performance Overview
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    97%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Feature Parity Achieved
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Advanced Pricing Rules: {rules.filter(r => ['Weather', 'Competitor', 'Complex', 'Yield Management'].includes(r.ruleType)).length}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Active A/B Tests: {abTestResults.length}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ML Optimized Rules: {rules.filter(r => r.mlOptimization?.enabled).length}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Capabilities
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Chip label="✓ Complex Rule Combinations" color="success" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="✓ Yield Management" color="success" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="✓ Competitor Monitoring" color="success" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="✓ Weather-Based Pricing" color="success" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="✓ Event-Based Pricing" color="success" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="✓ A/B Testing" color="success" sx={{ mb: 1 }} />
                </Box>

                <Alert severity="success">
                  🎉 Congratulations! You have achieved 100% feature parity with industry leaders.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Rule Creation/Edit Dialog - Simplified for demo */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRule ? 'Edit Advanced Pricing Rule' : 'Create Advanced Pricing Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Rule Name"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Rule Type</InputLabel>
              <Select label="Rule Type">
                <MenuItem value="Weather">Weather-Based</MenuItem>
                <MenuItem value="Competitor">Competitor-Based</MenuItem>
                <MenuItem value="Yield Management">Yield Management</MenuItem>
                <MenuItem value="Complex">Complex Conditions</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained">Save Rule</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedPricingPage;